import { DownloadTieService } from '../../ties/tie-details/tie-scorecard/download-tie/download-tie.service';
import { LeagueDetailsService } from './league-details.service';
import { SearchTeamListComponent } from './../../_shared/components/search-team-list/search-team-list.component';
import { AuthService } from '../../auth/auth.service';
import { Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ActionSheetController, IonRouterOutlet, ModalController, PopoverController } from '@ionic/angular';
import { finalize, takeUntil } from 'rxjs/operators';
import { CreateTeamFabComponent } from '../../my-teams/create-team-fab/create-team-fab.component';
import { TeamSelectorComponent } from '../../my-teams/team-selector/team-selector.component';
import { CreateTieComponent } from '../../ties/create-tie/create-tie.component';
import { TieSelectorComponent } from '../../ties/tie-selector/tie-selector.component';
import { ToastService } from '../../_core/services/toast.service';
import { SelectUserComponent } from '../../_shared/components/select-user/select-user.component';
import { League } from '../../_shared/models/league.model';
import { Team } from '../../_shared/models/team.model';
import { Tie } from '../../_shared/models/tie.model';
import { LeagueService } from '../league.service';
import { LeagueOptionsComponent } from './league-options/league-options.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CreateLeagueComponent } from '../create-league/create-league.component';
import { LeagueSettingComponent } from './league-setting/league-setting.component';
import { TieService } from '../../_core/services/tie.service';
import { Subject } from 'rxjs';
import { LeaguePolicyComponent } from './league-policy/league-policy.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  standalone: false,
    selector: 'app-league-details',
    templateUrl: './league-details.component.html',
    styleUrls: ['./league-details.component.scss'],
})
export class LeagueDetailsComponent implements OnInit {
    public authService = inject(AuthService);
    private router = inject(Router);
    private destroyRef = inject(DestroyRef);
    private translateService = inject(TranslateService);
     private readonly destroy$: Subject<void> = new Subject();

    @ViewChild('createTeam') createTeamFabComponent: CreateTeamFabComponent;

    leagueId: string;
    league: League;
    isLoading: boolean;
    isOwner: boolean;

    canFollow: any;
    showRemoveTeam = false;
    
    noTeamMatchesText = this.translateService.instant('You don\'t have any team matches');
    noTeamsText = this.translateService.instant('You don\'t have any teams');

    constructor(
        private leagueService: LeagueService,
        private route: ActivatedRoute,
        private modalController: ModalController,
        private routerOutlet: IonRouterOutlet,
        private actionSheetController: ActionSheetController,
        private toast: ToastService,
        private popoverController: PopoverController,
        private leagueDetailsService: LeagueDetailsService,
        private downloadTieService: DownloadTieService,
        private tieService: TieService
    ) { }

   async ngOnInit() {
        this.isLoading = true;
        this.leagueId = this.route.snapshot.params.leagueId;
        this.getLeague(null, true);
        this.tieService.subscribeDelete()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
            next: (tieId: string) => {
                this.league.ties = this.league.ties.filter(tie => tie._id !== tieId);
            }
        });
        this.subscriptions();
        
        // Subscribe to language changes to update text
        this.translateService.onLangChange.pipe(
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.noTeamMatchesText = this.translateService.instant('You don\'t have any team matches');
            this.noTeamsText = this.translateService.instant('You don\'t have any teams');
        });
    }

    getLeague(refresher?, skip: boolean = false) {
        const skipCache = skip || this.isOwner;
        this.leagueService
            .getLeague(this.leagueId, skipCache)
            .pipe(
                finalize(() => {
                    this.isLoading = false;
                    if (refresher) refresher.detail.complete();
                }),
                takeUntilDestroyed(this.destroyRef),

            )
            .subscribe({
                next: res => {
                    this.league = res.data;
                    this.leagueId = this.league._id;
                    this.isOwner = this.league.owners.some(owner => owner === this.authService.authUserSignal()?._id);
                    this.canFollow = !this.league.viewers.some(viewer => this.authService.authUserSignal()?._id === viewer);

                    this.sortLeagueTies()
                }
            });
    }

    private sortLeagueTies() {
        const completed: Tie[] = [];
        const upcoming: Tie[] = [];

        this.league.ties.forEach((tie) => {
            if (tie) {
                tie?.matchStatus === 'COMPLETED'
                    ? completed.push(tie)
                    : upcoming.push(tie);
            }
        });

        upcoming.sort((a, b) => (a.startDate > b.startDate) ? 1 : ((b.startDate > a.startDate) ? -1 : 0));
        completed.sort((a, b) => (b.startDate > a.startDate) ? 1 : ((a.startDate > b.startDate) ? -1 : 0));

        this.league.ties = [];

        upcoming.forEach(tie => {
            this.league.ties.push(tie);
        });

        completed.forEach(tie => {
            this.league.ties.push(tie);
        });
    }

    async addTie() {
        const actionSheet = await this.actionSheetController.create({
            header: this.translateService.instant('Add team match'),
            buttons: [{
                text: this.translateService.instant('New'),
                handler: () => {
                    this.createTie();
                }
            }, {
                text: this.translateService.instant('Existing'),
                handler: () => {
                    this.addExistingTie();
                }
            }]
        });

        await actionSheet.present();
    }

    async addTeam() {
        if (this.authService.authUserSignal().role.value < 400 && this.league.organisation?.organisationAbbreviation === 'ITA') {
            if (!this.league.organisation) {
                return;
            }
            this.openTeamSearchList();
        } else {
            const buttons = [{
                text: this.translateService.instant('New'),
                handler: () => {
                    this.createTeamFabComponent.createTeam(this.league._id);
                }
            }, {
                text: this.translateService.instant('Existing'),
                handler: () => {
                    this.addExistingTeam();
                }
            }, {
                text: this.translateService.instant('Search'),
                handler: () => {
                    this.openTeamSearchList();
                }
            }];
    
            const actionSheet = await this.actionSheetController.create({
                header: this.translateService.instant('Add team'),
                buttons
            });
    
            await actionSheet.present();
        }
    }

    private async openTeamSearchList() {
        const modal = await this.modalController.create({
            component: SearchTeamListComponent,    
        });
    
        await modal.present();

        const { data } = await modal.onDidDismiss();

        if (data) {
            this.addTeamsToLeague([data]);
        }
    }

    private async addExistingTie() {
        const modal = await this.modalController.create({
            component: TieSelectorComponent,
            componentProps: {
                leagueId: this.leagueId
            },
            presentingElement: this.routerOutlet.nativeEl
        });

        await modal.present();
        const { data } = await modal.onDidDismiss();

        if (data) {
            // this.isLoading = true;
            this.leagueService
                .addTiesToLeague(this.leagueId, data)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(
                    res => {
                        const ties = res.ties || [];
                        ties.forEach((tieId: string) => {
                            if (this.league.ties && !this.league.ties.some(t => t._id === tieId)) {
                                this.league.ties.push({ _id: tieId } as Tie);
                            }
                        });
                        this.getLeague(null, true);
                        this.toast.presentToast(res.message);
                    }
                );
        }
    }

    private async createTie() {
        const modal = await this.modalController.create({
            component: CreateTieComponent,
            componentProps: {
                leagueId: this.leagueId,
                teams: this.league.teams,
                isHidePolicy: true
            },
            presentingElement: this.routerOutlet.nativeEl
        });

        await modal.present();
        const { data } = await modal.onDidDismiss();

        if (data) {
            this.league.ties.unshift(data);
        }
    }

    private async addExistingTeam() {
        const modal = await this.modalController.create({
            component: TeamSelectorComponent,
            componentProps: {
                leagueId: this.leagueId
            },
            presentingElement: this.routerOutlet.nativeEl
        });

        await modal.present();
        const { data } = await modal.onDidDismiss();

        if (data) {
            this.addTeamsToLeague(data);
        }
    }

    private addTeamsToLeague(teams: Team[]) {
        this.leagueService
        .addTeamsToLeague(this.leagueId, teams)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(
            res => {
                res.data.forEach((team: Team) => {
                    if (this.league.teams.findIndex(currentTeam => currentTeam._id === team._id) === -1) {
                        this.league.teams.unshift(team);
                    }
                });
                this.toast.presentToast(res.message);
            }
        );
    }

    removeTeamFromLeague(teamId: string, index) {
        this.leagueService
        .removeTeamFromLeague(this.leagueId, teamId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(
            res => {
                this.league.teams.splice(index, 1)
                this.toast.presentToast(res.message);
            }
        );
    }

    async openLeagueOptions(ev: any) {
        const popover = await this.popoverController.create({
            component: LeagueOptionsComponent,
            componentProps: {
                isOwner:  this.isOwner,
            },
            event: ev,
            translucent: false
        });

        await popover.present();

        const { data } = await popover.onDidDismiss();

        switch (data) {
            case 'add-owner':
                await this.getUserByPhone()
                break;
            case 'update-league':
                await this.updateLeague()
                break;
            case 'remove-team':
                this.showRemoveTeam = !this.showRemoveTeam;
                break;
            case 'settings':
                this.openLeagueSettings();
                break;
            case 'policy':
                await this.policy()
                break;
            default:
                break;
        }
    }

    async policy() {
        const modal = await this.modalController.create({
            component: LeaguePolicyComponent,
            componentProps: {
                leaguePolicy: this.league.policy,
                isOwner:  this.isOwner,
            },
        });
        await modal.present();
        const { data } = await modal.onDidDismiss();
        if (data) {
            this.league.policy = data;
        }
    }

    async openLeagueSettings() {
        const modal = await this.modalController.create({
            component: LeagueSettingComponent,
            componentProps: {
                league: this.league,
            }
        });
        await modal.present();

        const { data } = await modal.onDidDismiss();

        if (data) {
            this.league.settings = data;
        }
    }

    private async getUserByPhone() {
        const modal = await this.modalController.create({
            component: SelectUserComponent,
            presentingElement: await this.modalController.getTop()
        });

        await modal.present();

        const { data } = await modal.onDidDismiss();

        if (data) {
            const phone = data.phone ? data.country_code + data.phone : null;
            const email = data.email;

            this.leagueService
                .addOwner(this.leagueId, phone, email)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: res => this.toast.presentToast(res.message)
                });
        }
    }

    private async updateLeague() {
        const modal = await this.modalController.create({
            component: CreateLeagueComponent,
            componentProps: {
                isEdit: true,
                league: this.league
            },
            presentingElement: await this.modalController.getTop()
        });

        await modal.present();

        const { data } = await modal.onDidDismiss();
// update league api is calling in create-league.component here after the modal is dismissed api response is returned
        if (data) {
            this.league.policy = data?.policy;
            this.league.organisation = data?.organisation;
        }
    }

    changeFollow() {
        if (this.canFollow) {
            this.leagueDetailsService
                .follow(this.league._id)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: res => {
                        this.canFollow = false;
                        this.league.viewers.push(this.authService.authUserSignal()?._id || '');
                    }
                });
        } else {
            this.leagueDetailsService
                .unfollow(this.league._id)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: res => {
                        this.canFollow = true;
                        this.league.viewers.forEach((viewer, i) => {
                            if (viewer === this.authService.authUserSignal()?._id || '') {
                                this.league.viewers.splice(i, 1);
                            }
                        });
                    },
                });
        }
    }

    navigateToTeamDetailsPage(team: Team) {
        if (!this.showRemoveTeam) {
            this.router.navigate(['/leagues', this.league._id, 'teams', team._id, 'schedule'])
        }
    }

    private subscriptions() {
        this.downloadTieService
            .subscribeDownloadedTie()
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (tie: Tie) => {
                    this.league.ties.push(tie);
                }
            })
    }
 
}
